/**
 * frontend/src/core/tipos/tiposOrganizacao.ts
 *
 * Tipos TypeScript para sistema de organizações multi-tenant.
 *
 * CONTEXTO:
 * Estes tipos descrevem os contratos trocados entre frontend e backend
 * para gerenciamento de organizações (escritórios de advocacia),
 * membros e convites.
 *
 * PADRÕES:
 * - Nomes longos e descritivos em português
 * - Timestamps vindos da API são strings ISO-8601
 * - Roles seguem hierarquia: owner > admin > member
 */

/**
 * Roles disponíveis dentro de uma organização.
 * 
 * Hierarquia de permissões:
 * - owner: controle total (deletar org, promover admins, transferir ownership)
 * - admin: gestão de membros (convidar, remover, alterar roles de members)
 * - member: acesso aos recursos compartilhados (leitura/escrita de processos)
 */
export type RoleOrganizacao = 'owner' | 'admin' | 'member';

/**
 * Status possíveis de um convite.
 */
export type StatusConvite = 'pendente' | 'aceito' | 'expirado' | 'cancelado';

/**
 * Representa uma organização (escritório de advocacia).
 */
export interface Organizacao {
  /** Identificador único (UUID) */
  id: string;
  /** Nome da organização para exibição */
  nome: string;
  /** Identificador URL-safe único */
  slug: string;
  /** Descrição opcional da organização */
  descricao: string | null;
  /** Data de criação (ISO 8601) */
  data_criacao: string;
  /** Data da última atualização (ISO 8601) */
  data_atualizacao: string;
  /** Indicador se a organização está ativa */
  ativa: boolean;
  /** Total de membros na organização (preenchido em listagens) */
  total_membros?: number;
  /** Role do usuário atual nesta organização (preenchido em listagens) */
  meu_role?: RoleOrganizacao;
}

/**
 * Representa um membro de uma organização.
 */
export interface MembroOrganizacao {
  /** Identificador único da relação membro-organização */
  id: string;
  /** ID do usuário membro */
  usuario_id: string;
  /** Nome do usuário para exibição */
  nome_usuario: string;
  /** Email do usuário */
  email_usuario: string;
  /** Role do usuário na organização */
  role: RoleOrganizacao;
  /** Data de entrada na organização (ISO 8601) */
  data_entrada: string;
  /** ID do usuário que convidou este membro (se aplicável) */
  convidado_por: string | null;
  /** Nome de quem convidou (para exibição) */
  nome_convidado_por: string | null;
}

/**
 * Representa um convite pendente para organização.
 */
export interface ConviteOrganizacao {
  /** Identificador único do convite */
  id: string;
  /** Token do convite (para URL) */
  token: string;
  /** ID da organização */
  organizacao_id: string;
  /** Nome da organização (para exibição) */
  nome_organizacao: string;
  /** Email do convidado */
  email: string;
  /** Role que será atribuído ao aceitar */
  role_atribuido: 'admin' | 'member';
  /** ID de quem convidou */
  convidado_por: string;
  /** Nome de quem convidou */
  nome_convidado_por: string;
  /** Data de criação do convite (ISO 8601) */
  data_criacao: string;
  /** Data de expiração do convite (ISO 8601) */
  expira_em: string;
  /** Status atual do convite */
  status: StatusConvite;
  /** Link completo do convite (opcional) */
  link_convite?: string;
}

// ===== Tipos de Request =====

/**
 * Dados para criar uma nova organização.
 */
export interface DadosCriacaoOrganizacao {
  /** Nome da organização */
  nome: string;
  /** Slug URL-safe (opcional, gerado automaticamente se não fornecido) */
  slug?: string;
  /** Descrição opcional */
  descricao?: string;
}

/**
 * Dados para atualizar uma organização.
 */
export interface DadosAtualizacaoOrganizacao {
  /** Novo nome da organização */
  nome?: string;
  /** Nova descrição */
  descricao?: string;
}

/**
 * Dados para enviar um convite.
 */
export interface DadosEnvioConvite {
  /** Email do usuário a ser convidado */
  email: string;
  /** Role a ser atribuído (member ou admin) */
  role?: 'admin' | 'member';
}

/**
 * Dados para alterar role de um membro.
 */
export interface DadosAlteracaoRole {
  /** Novo role a ser atribuído */
  novo_role: 'admin' | 'member';
}

/**
 * Dados para transferir ownership.
 */
export interface DadosTransferenciaOwnership {
  /** ID do novo owner */
  novo_owner_id: string;
}

// ===== Tipos de Response =====

/**
 * Resposta de aceitação de convite.
 */
export interface RespostaAceitarConvite {
  mensagem: string;
  organizacao_id: string;
  role: RoleOrganizacao;
}

/**
 * Resposta genérica de sucesso.
 */
export interface RespostaSucesso {
  mensagem: string;
}

// ===== Tipos de Estado (Context) =====

/**
 * Estado da organização no contexto de autenticação.
 */
export interface EstadoOrganizacao {
  /** Organização atual selecionada (null se usuário solo) */
  organizacaoAtual: Organizacao | null;
  /** Lista de organizações do usuário */
  organizacoes: Organizacao[];
  /** Role do usuário na organização atual */
  roleAtual: RoleOrganizacao | null;
  /** Indica se está carregando dados de organização */
  carregando: boolean;
}

/**
 * Ações disponíveis para gerenciar organizações no contexto.
 */
export interface AcoesOrganizacao {
  /** Carrega/atualiza a lista de organizações do usuário */
  carregarOrganizacoes: () => Promise<void>;
  /** Seleciona uma organização como ativa */
  selecionarOrganizacao: (organizacaoId: string | null) => void;
  /** Cria uma nova organização */
  criarOrganizacao: (dados: DadosCriacaoOrganizacao) => Promise<Organizacao>;
  /** Sai da organização atual */
  sairDaOrganizacao: (organizacaoId: string) => Promise<void>;
}

// ===== Utilitários de Tipo =====

/**
 * Verifica se o role tem permissão de administrador (owner ou admin).
 */
export function ehAdministrador(role: RoleOrganizacao | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Verifica se o role é owner.
 */
export function ehOwner(role: RoleOrganizacao | null | undefined): boolean {
  return role === 'owner';
}

/**
 * Retorna label traduzido para o role.
 */
export function labelRole(role: RoleOrganizacao): string {
  const labels: Record<RoleOrganizacao, string> = {
    owner: 'Proprietário',
    admin: 'Administrador',
    member: 'Membro',
  };
  return labels[role];
}
